import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { 
  contractApi, 
  SigningSessionDto, 
  SubmitSignaturesDto,
  SigningResultDto,
  validateAccessCode
} from '../services/contractService';
import SignaturePad from '../components/SignaturePad';
import { useTenantConfig } from '../contexts/TenantConfigContext';

interface SigningPageParams extends Record<string, string | undefined> {
  documentSetId: string;
}

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  direction: rtl;
`;

const Card = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  width: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  background: white;
  color: var(--primary-color);
  padding: 30px;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const SigningLogo = styled.img`
  width: 100%;
  height: 80px;
  object-fit: contain;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;


const Content = styled.div`
  padding: 30px;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  gap: 20px;
`;

const Step = styled.div<{ $active?: boolean; $completed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 20px;
    left: -20px;
    width: 20px;
    height: 2px;
    background: ${props => props.$completed ? 'var(--success-color)' : 'var(--border-color)'};
  }
`;

const StepCircle = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: white;
  background: ${props => {
    if (props.$completed) return 'var(--success-color)';
    if (props.$active) return 'var(--primary-color)';
    return 'var(--border-color)';
  }};
`;

const StepLabel = styled.span<{ $active?: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-secondary)'};
`;

// Access Code Verification Step
const AccessCodeForm = styled.div`
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
`;

const AccessCodeInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 15px 20px;
  font-size: 18px;
  text-align: center;
  letter-spacing: 2px;
  border: 2px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  margin: 20px 0;
  font-weight: 600;
  text-transform: uppercase;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

// Document Signing Step
const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DocumentCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  overflow: hidden;
`;

const DocumentHeader = styled.div`
  background: var(--background-color);
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DocumentTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
`;

const SignedBadge = styled.span`
  background: var(--success-color);
  color: white;
  padding: 4px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 600;
`;

const DocumentContent = styled.div`
  padding: 20px;
  max-height: 400px;
  overflow-y: auto;
  
  iframe {
    width: 100%;
    height: 300px;
    border: none;
  }
`;

const SignatureSection = styled.div`
  padding: 20px;
  background: var(--background-light);
  border-top: 1px solid var(--border-color);
`;

const SignatureLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 10px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger'; fullWidth?: boolean }>`
  padding: 12px 24px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  ${props => props.fullWidth && 'width: 100%;'}
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'success':
        return `
          background: var(--success-color);
          color: white;
          &:hover:not(:disabled) { background: #059669; }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover:not(:disabled) { background: #dc2626; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin: 16px 0;
  border: 1px solid var(--error-color);
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: var(--success-light);
  color: var(--success-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin: 16px 0;
  border: 1px solid var(--success-color);
  text-align: center;
`;

const Checkbox = styled.input`
  margin-left: 8px;
  transform: scale(1.2);
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
`;

const ExpiryWarning = styled.div`
  background: var(--warning-light);
  color: var(--warning-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin: 16px 0;
  border: 1px solid var(--warning-color);
  text-align: center;
  font-size: 14px;
`;

const SigningPage: React.FC = () => {
  // טעינת הגדרות החברה (per-tenant) - לוגו נטען מ-SystemParameters
  const { config } = useTenantConfig();
  const { documentSetId } = useParams<SigningPageParams>();

  const [step, setStep] = useState<'access' | 'signing' | 'completed'>('access');
  const [accessCode, setAccessCode] = useState('');
  const [signingToken, setSigningToken] = useState('');
  const [session, setSession] = useState<SigningSessionDto | null>(null);
  const [signatures, setSignatures] = useState<Record<number, string>>({}); // חתימות ראשיות (אב בחוזה ילדים)
  const [motherSignatures, setMotherSignatures] = useState<Record<number, string>>({}); // חתימות אם - לחוזה ילדים
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<SigningResultDto | null>(null);

  useEffect(() => {
    if (!documentSetId) {
      setError('מזהה מסמך לא תקין');
      return;
    }
  }, [documentSetId]);

  const handleAccessCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentSetId || !validateAccessCode(accessCode)) {
      setError('קוד גישה לא תקין');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await contractApi.verifyAccessCode(documentSetId, { accessCode });
      setSigningToken(response.token);
      
      // Load signing session
      const sessionData = await contractApi.getSigningSession(documentSetId, response.token);
      setSession(sessionData);
      setStep('signing');
      
    } catch (err: any) {
      let errorMessage = 'קוד גישה שגוי';
      
      if (err.response?.status === 401) {
        errorMessage = err.response?.data?.message || 'קוד גישה שגוי או פג תוקף';
      } else if (err.response?.status === 404) {
        errorMessage = 'מסמך לא נמצא או פג תוקף';
      } else if (err.response?.status >= 500) {
        errorMessage = 'שגיאה במערכת, אנא נסה שוב מאוחר יותר';
      } else {
        errorMessage = err.response?.data?.message || 'אירעה שגיאה, אנא בדוק את הקוד ונסה שוב';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureChange = (documentId: number, signature: string | null) => {
    setSignatures(prev => {
      const newSignatures = { ...prev };
      if (signature) {
        newSignatures[documentId] = signature;
      } else {
        delete newSignatures[documentId];
      }
      return newSignatures;
    });
  };

  // טיפול בחתימת האם - לחוזה ילדים
  const handleMotherSignatureChange = (documentId: number, signature: string | null) => {
    setMotherSignatures(prev => {
      const newSignatures = { ...prev };
      if (signature) {
        newSignatures[documentId] = signature;
      } else {
        delete newSignatures[documentId];
      }
      return newSignatures;
    });
  };


  const handleSubmitSignatures = async () => {
    if (!session || !signingToken || !documentSetId) return;

    if (!acceptTerms) {
      setError('יש לאשר את התנאים כדי להמשיך');
      return;
    }

    const requiredDocuments = session.documents.filter(d => d.requiresSignature && !d.isSigned);
    const missingSignatures = requiredDocuments.filter(d => !signatures[d.id]);

    if (missingSignatures.length > 0) {
      setError('יש לחתום על כל המסמכים הנדרשים');
      return;
    }

    // בדיקה שיש חתימת אם לכל מסמך שדורש שתי חתימות
    const missingMotherSignatures = requiredDocuments.filter(
      d => d.requiresDualSignature && !motherSignatures[d.id]
    );

    if (missingMotherSignatures.length > 0) {
      setError('יש לחתום גם את חתימת האם על כל המסמכים');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const submitData: SubmitSignaturesDto = {
        signatures: Object.entries(signatures).map(([documentId, signatureData]) => ({
          documentId: parseInt(documentId),
          signatureData,
          motherSignatureData: motherSignatures[parseInt(documentId)] || undefined
        })),
        acceptTerms,
        clientIdNumber: session.clientIdNumber // Use ID number from session
      };

      const resultData = await contractApi.submitSignatures(documentSetId, submitData, signingToken);
      setResult(resultData);

      if (resultData.success) {
        setStep('completed');
      } else {
        setError(resultData.message);
      }

    } catch (err: any) {
      let errorMessage = 'אירעה שגיאה בשמירת החתימות';

      if (err.response?.status === 401) {
        errorMessage = 'פג תוקף הקישור, אנא בקש קישור חדש';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'נתונים לא תקינים, אנא בדוק ונסה שוב';
      } else if (err.response?.status >= 500) {
        errorMessage = 'שגיאה במערכת, אנא נסה שוב מאוחר יותר';
      } else {
        errorMessage = err.response?.data?.message || 'אירעה שגיאה בשמירת החתימות';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isExpired = session && new Date(session.expiresAt) < new Date();
  const timeUntilExpiry = session ? Math.max(0, new Date(session.expiresAt).getTime() - new Date().getTime()) : 0;
  const hoursUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60 * 60));

  return (
    <Container>
      <Card>
        <Header>
          <LogoContainer>
            <SigningLogo
              src={config?.logoFilename ? `/${config.logoFilename}` : '/logobizchut.png'}
              alt={config?.companyNameHebrew || 'לוגו'}
              loading="eager"
            />
          </LogoContainer>
          <Title>חתימה דיגיטלית</Title>
        </Header>

        <Content>
          <StepIndicator>
            <Step $completed={step !== 'access'} $active={step === 'access'}>
              <StepCircle $completed={step !== 'access'} $active={step === 'access'}>
                {step !== 'access' ? '✓' : '1'}
              </StepCircle>
              <StepLabel $active={step === 'access'}>אימות קוד</StepLabel>
            </Step>
            <Step $completed={step === 'completed'} $active={step === 'signing'}>
              <StepCircle $completed={step === 'completed'} $active={step === 'signing'}>
                {step === 'completed' ? '✓' : '2'}
              </StepCircle>
              <StepLabel $active={step === 'signing'}>חתימה</StepLabel>
            </Step>
            <Step $active={step === 'completed'}>
              <StepCircle $active={step === 'completed'}>
                {step === 'completed' ? '✓' : '3'}
              </StepCircle>
              <StepLabel $active={step === 'completed'}>סיום</StepLabel>
            </Step>
          </StepIndicator>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {step === 'access' && (
            <AccessCodeForm>
              <h2>הזן קוד גישה</h2>
              <p>הזן את קוד הגישה שקיבלת במייל כדי לגשת למסמכים</p>
              
              <form onSubmit={handleAccessCodeSubmit}>
                <AccessCodeInput
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="הזן קוד גישה"
                  maxLength={20}
                  hasError={!!error}
                  disabled={loading}
                />
                <Button 
                  type="submit" 
                  variant="primary" 
                  fullWidth 
                  disabled={loading || !validateAccessCode(accessCode)}
                >
                  {loading ? 'מאמת...' : 'המשך'}
                </Button>
              </form>
            </AccessCodeForm>
          )}

          {step === 'signing' && session && (
            <>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h2>שלום {session.clientName}</h2>
                <p>אנא עיין במסמכים הבאים וחתום עליהם</p>
                {isExpired && (
                  <ErrorMessage>
                    תוקף הקישור פג. אנא צור קשר עם הנציג שלך.
                  </ErrorMessage>
                )}
                {!isExpired && hoursUntilExpiry < 2 && (
                  <ExpiryWarning>
                    ⏰ תוקף הקישור יפוג בעוד {hoursUntilExpiry === 0 ? 'פחות משעה' : `${hoursUntilExpiry} שעות`}
                  </ExpiryWarning>
                )}
              </div>

              <DocumentsList>
                {session.documents.map(document => (
                  <DocumentCard key={document.id}>
                    <DocumentHeader>
                      <DocumentTitle>{document.title}</DocumentTitle>
                      {document.isSigned && <SignedBadge>חתום ✓</SignedBadge>}
                    </DocumentHeader>
                    
                    <DocumentContent>
                      <div dangerouslySetInnerHTML={{ __html: document.htmlContent }} />
                    </DocumentContent>

                    {document.requiresSignature && !document.isSigned && !isExpired && (
                      <SignatureSection>
                        {document.requiresDualSignature ? (
                          <>
                            <SignatureLabel>חתימת האב:</SignatureLabel>
                            <SignaturePad
                              onSignatureChange={(signature) => handleSignatureChange(document.id, signature)}
                              disabled={loading}
                              required={true}
                              width={400}
                              height={150}
                            />

                            <SignatureLabel style={{ marginTop: '24px' }}>חתימת האם:</SignatureLabel>
                            <SignaturePad
                              onSignatureChange={(signature) => handleMotherSignatureChange(document.id, signature)}
                              disabled={loading}
                              required={true}
                              width={400}
                              height={150}
                            />
                          </>
                        ) : (
                          <>
                            <SignatureLabel>חתימה נדרשת:</SignatureLabel>
                            <SignaturePad
                              onSignatureChange={(signature) => handleSignatureChange(document.id, signature)}
                              disabled={loading}
                              required={true}
                              width={400}
                              height={150}
                            />
                          </>
                        )}
                      </SignatureSection>
                    )}
                  </DocumentCard>
                ))}
              </DocumentsList>

              {!isExpired && (
                <>

                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      disabled={loading}
                    />
                    <span>אני מאשר שקראתי והבנתי את כל המסמכים ומסכים לתנאים</span>
                  </CheckboxLabel>

                  <Button
                    variant="success"
                    fullWidth
                    onClick={handleSubmitSignatures}
                    disabled={loading || !acceptTerms || Object.keys(signatures).length === 0}
                  >
                    {loading ? 'שולח חתימות...' : 'שלח חתימות'}
                  </Button>
                </>
              )}
            </>
          )}

          {step === 'completed' && result && (
            <div style={{ textAlign: 'center' }}>
              <SuccessMessage>
                <h2>🎉 חתימה הושלמה בהצלחה!</h2>
                <p>{result.message}</p>
                <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--background-light)', borderRadius: 'var(--border-radius-md)', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  ניתן לסגור את הדפדפן או הכרטיסייה
                </div>
              </SuccessMessage>
            </div>
          )}
        </Content>
      </Card>
    </Container>
  );
};

export default SigningPage;